import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useAPI, useLiveRef, usePrevious } from '@gitbook/util-react-hooks';

import { getPaletteId } from './id';
import { openedPaletteAtom, paletteOpenedByIdSelector } from './state';
import { createPalette, executeCommandInPalette, typeQueryInPalette } from './transforms';
import type { PaletteAnchorRef, PaletteCommandDef, PaletteDef, VisiblePalette } from './types';

/**
 * A command palette handle is an instance of the palette that can be used
 * in specific components.
 */
export interface PaletteHandle {
    /**
     * Whether the palette associated with the anchor is opened or not.
     */
    opened: boolean;

    /**
     * Open the commands palette.
     * If no palette is provided, the default palette will open.
     */
    open: (palette: PaletteDef) => void;

    /**
     * Execute a command.
     */
    executeCommand: (command: PaletteCommandDef) => Promise<void>;

    /**
     * Close the commands palette.
     */
    close: () => void;

    /**
     * Update the search query in the palette.
     */
    typeQuery: (query: string) => void;
}

/**
 * Hook to create a palette handle.
 * When providing an anchor, the handle can be used to open a specific commands palette next to it.
 */
export function usePaletteHandle(
    anchorRef: PaletteAnchorRef,
    options: Pick<VisiblePalette, 'inputRef'> & {
        /** Callback when the palette is closed */
        onClose?: () => void;
    } = {}
): PaletteHandle {
    const { onClose, inputRef } = options;

    const id = getPaletteId(anchorRef);
    const opened = useRecoilValue(paletteOpenedByIdSelector(id));
    const setPalette = useSetRecoilState(openedPaletteAtom);
    const refs = useLiveRef({ id, opened, onClose, anchorRef, inputRef });

    // Trigger onClose when the palette is closed
    const prevOpened = usePrevious(opened);
    React.useEffect(() => {
        if (!opened && prevOpened) {
            refs.current.onClose?.();
        }
    }, [opened, prevOpened, refs]);

    // When it's unmounted, close the palette if it has been opened by this handle.
    React.useEffect(() => {
        return () => {
            const { id, opened } = refs.current;
            if (!opened) {
                return;
            }
            setPalette((prev) => {
                if (prev && prev.id === id) {
                    return null;
                }
                return prev;
            });
        };
    }, [setPalette, refs]);

    // Create the API

    const open: PaletteHandle['open'] = (palette: PaletteDef) => {
        const { id, anchorRef, inputRef } = refs.current;
        setPalette((prev) => {
            if (prev && prev.palette === palette && prev.id === id) {
                return prev;
            }
            return { palette, id, anchorRef, inputRef };
        });
    };

    const close: PaletteHandle['close'] = () => {
        setPalette(null);
    };

    const typeQuery: PaletteHandle['typeQuery'] = (query: string) => {
        setPalette((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                palette: typeQueryInPalette(prev.palette, query),
            };
        });
    };

    const executeCommand: PaletteHandle['executeCommand'] = async (command) => {
        const { id, anchorRef, inputRef } = refs.current;
        const palette = await executeCommandInPalette(createPalette({}), command);
        setPalette(() => {
            return palette ? { palette, id, anchorRef, inputRef } : null;
        });
    };

    const api = useAPI({
        opened,
        open,
        close,
        typeQuery,
        executeCommand,
    });
    api.opened = opened;
    return api;
}
